import React, { useState, useMemo, useRef, useEffect } from 'react';
import { WATCHES, BRANDS, MODELS_BY_BRAND, getAvailableYears } from '../data';
import WatchCard from '../components/WatchCard';
import MarketTicker from '../components/MarketTicker';
import WatchNewsFeed from '../components/WatchNewsFeed';

// Dynamic years — only years that actually have watches in the catalog
const ALL_YEARS = [...new Set(WATCHES.map(w => w.year))].sort((a,b) => b - a);

// Dynamic nicknames — all unique nicknames sorted A-Z
const ALL_NICKNAMES = ['All', ...new Set(
  WATCHES
    .filter(w => w.nickname)
    .map(w => w.nickname)
)].sort((a,b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b));

const sel = {
  fontSize: 13, padding: '10px 12px', borderRadius: 'var(--radius)',
  border: '0.5px solid var(--border2)', background: 'var(--surface)',
  color: 'var(--text)', outline: 'none', cursor: 'pointer',
  fontFamily: "'Space Grotesk', sans-serif", width: '100%',
};

export default function MarketPage({ onSelectWatch }) {
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('All');
  const [model, setModel] = useState('All Models');
  const [year, setYear] = useState('All');
  const [sort, setSort] = useState('market-desc');
  const [nickname, setNickname] = useState('All');
  const resultsRef = useRef(null);

  const modelOptions = brand !== 'All' ? (MODELS_BY_BRAND[brand] || ['All Models']) : ['All Models'];

  // Dynamic years — only show years that exist in the current brand/model filter
  const availableYears = useMemo(() => {
    let pool = WATCHES;
    if (brand !== 'All') pool = pool.filter(w => w.brand === brand);
    if (model !== 'All Models') pool = pool.filter(w => w.model.toLowerCase().includes(model.toLowerCase().replace('all models','').trim()));
    return getAvailableYears(pool);
  }, [brand, model]);

  function handleBrandChange(b) {
    setBrand(b);
    setModel('All Models');
    setYear('All');
  }

  const hasQuery = search.trim().length > 0 || brand !== 'All' || year !== 'All' || model !== 'All Models' || nickname !== 'All';

  const filtered = useMemo(() => {
    if (!hasQuery) return [];
    const q = search.toLowerCase().trim();
    return WATCHES.filter(w => {
      const qNorm = q.replace(/-/g, ' ').replace(/\s+/g, ' ');
      const matchSearch = !q || [
        w.model.replace(/-/g,' '), w.brand, w.ref,
        w.nickname || '', w.colorway || '',
        w.condition,
        ...(w.tags || []),
      ].some(field => field.toLowerCase().includes(qNorm));
      const matchBrand = brand === 'All' || w.brand === brand;
      const modelQ = model.toLowerCase().replace('all models','').trim();
      const matchModel = !model || model === 'All Models' || !modelQ ||
        w.model.toLowerCase().includes(modelQ) ||
        (w.nickname && w.nickname.toLowerCase().includes(modelQ));
      const matchYear = year === 'All' || w.year === Number(year);
      const matchNickname = nickname === 'All' || (w.nickname && w.nickname.toLowerCase().includes(nickname.toLowerCase()));
      return matchSearch && matchBrand && matchModel && matchYear && matchNickname;
    }).sort((a, b) => {
      if (sort === 'market-desc') return b.market - a.market;
      if (sort === 'market-asc') return a.market - b.market;
      if (sort === 'brand') return a.brand.localeCompare(b.brand);
      if (sort === 'year-desc') return b.year - a.year;
      if (sort === 'year-asc') return a.year - b.year;
      return ((b.market - b.msrp) / b.msrp) - ((a.market - a.msrp) / a.msrp);
    });
  }, [search, brand, model, year, sort, nickname, hasQuery]);

  // Scroll results to top of search panel when results change
  useEffect(() => {
    if (hasQuery && filtered.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [filtered.length, hasQuery]);

  function clearAll() {
    setSearch(''); setBrand('All'); setModel('All Models'); setYear('All'); setNickname('All');
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px', position: 'relative', zIndex: 1 }}>

      {/* Hero */}
      <div style={{ padding: 'clamp(28px,5vw,56px) 0 32px', borderBottom: '0.5px solid var(--border)', marginBottom: 24 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C0392B', background: 'rgba(192,57,43,0.08)', padding: '4px 10px', borderRadius: 99, marginBottom: 14, fontFamily: "'Space Mono', monospace" }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#C0392B', display: 'inline-block' }} /> Live market data
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(26px,5vw,40px)', fontWeight: 700, lineHeight: 1.15, marginBottom: 12, letterSpacing: '-0.02em' }}>
          Know exactly what<br />your watch is worth.
        </h1>
        <p style={{ fontSize: 'clamp(13px,2vw,15px)', color: 'var(--text2)', lineHeight: 1.7, marginBottom: 20 }}>
          Market price · Trade value · Insurance payout — {WATCHES.length} watches · 2000–2026
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => document.querySelector('input[type=text]')?.focus()}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #C0392B, #96281B)',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif",
              boxShadow: '0 4px 14px rgba(192,57,43,0.25)',
            }}
          >
            <i className="ti ti-search" style={{ marginRight: 6 }} />Search watches
          </button>
          <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Space Mono', monospace" }}>
            Prices sourced from Chrono24 · WatchCharts · Reddit WatchExchange
          </div>
        </div>
      </div>

      {/* Search panel — results scroll here */}
      <div ref={resultsRef} style={{ background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-xl)', padding: 'clamp(16px,3vw,24px)', marginBottom: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: "'Space Mono', monospace", marginBottom: 14 }}>
          Search {WATCHES.length} watches — results appear instantly
        </div>

        {/* Text search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface2)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius)', padding: '11px 14px', marginBottom: 12 }}>
          <i className="ti ti-search" style={{ color: 'var(--text3)', fontSize: 16, flexShrink: 0 }} />
          <input
            type="text"
            placeholder='Try "Hulk", "Pepsi", "Panda", "126610LN", "Rolex 2019", "green dial"…'
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoComplete="off"
            style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: 14, color: 'var(--text)', fontFamily: "'Space Grotesk', sans-serif", minWidth: 0 }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', color: 'var(--text3)', fontSize: 14, flexShrink: 0, cursor: 'pointer' }}>
              <i className="ti ti-x" />
            </button>
          )}
        </div>

        {/* Dropdowns */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', gap: 8, marginBottom: 12 }}>
          <select value={brand} onChange={e => handleBrandChange(e.target.value)} style={sel}>
            <option value="All">All brands</option>
            {BRANDS.filter(b => b !== 'All').map(b => <option key={b} value={b}>{b}</option>)}
          </select>

          <select value={model} onChange={e => { setModel(e.target.value); setYear('All'); }} style={{ ...sel, opacity: brand === 'All' ? 0.5 : 1 }} disabled={brand === 'All'}>
            {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          {/* Dynamic year — only shows years with actual watches */}
          <select value={year} onChange={e => setYear(e.target.value)} style={sel}>
            <option value="All">All years ({availableYears.length > 0 ? `${Math.min(...availableYears)}–${Math.max(...availableYears)}` : '2000–2026'})</option>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <select value={sort} onChange={e => setSort(e.target.value)} style={sel}>
            <option value="market-desc">Highest value</option>
            <option value="market-asc">Lowest value</option>
            <option value="premium">Biggest premium</option>
            <option value="year-desc">Newest first</option>
            <option value="year-asc">Oldest first</option>
            <option value="brand">Brand A–Z</option>
          </select>
        </div>

        {hasQuery && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Space Mono', monospace" }}>
              {filtered.length > 0 ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''}` : 'No results'}
            </span>
            <button onClick={clearAll} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 'var(--radius)', border: '0.5px solid var(--border2)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" }}>
              <i className="ti ti-x" style={{ marginRight: 4, fontSize: 10 }} />Clear
            </button>
          </div>
        )}

        {/* ── RESULTS INLINE INSIDE SEARCH PANEL ── */}
        {hasQuery && filtered.length > 0 && (
          <div style={{ marginTop: 20, borderTop: '0.5px solid var(--border)', paddingTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
              {filtered.map(w => <WatchCard key={w.id} watch={w} onClick={() => onSelectWatch(w)} />)}
            </div>
          </div>
        )}

        {hasQuery && filtered.length === 0 && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '0.5px solid var(--border)', textAlign: 'center', color: 'var(--text3)', padding: '32px 0' }}>
            <i className="ti ti-search-off" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontFamily: "'Space Grotesk', sans-serif", marginBottom: 6 }}>No watches match</div>
            <div style={{ fontSize: 12, marginBottom: 12 }}>
              {year !== 'All' ? `No ${brand !== 'All' ? brand : 'watches'} found for ${year}. Try a different year — available: ${availableYears.slice(0,8).join(', ')}${availableYears.length > 8 ? '…' : ''}` : 'Try a shorter search'}
            </div>
          </div>
        )}
      </div>

      {/* Brand pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap', overflowX: 'auto', paddingBottom: 4 }}>
        {BRANDS.map(b => (
          <button key={b} onClick={() => handleBrandChange(b)} style={{
            fontSize: 12, padding: '5px 14px', borderRadius: 99,
            border: '0.5px solid var(--border2)', whiteSpace: 'nowrap',
            background: brand === b ? 'var(--text)' : 'var(--surface)',
            color: brand === b ? 'var(--surface)' : 'var(--text2)',
            cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500,
          }}>{b}</button>
        ))}
      </div>

      {/* Ticker + News */}
      <MarketTicker onSelectWatch={onSelectWatch} />
      <WatchNewsFeed />

      {/* Empty state */}
      {!hasQuery && (
        <div style={{ textAlign: 'center', padding: 'clamp(32px,8vw,64px) 0' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⌚</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(16px,3vw,18px)', fontWeight: 600, marginBottom: 8 }}>
            Search {WATCHES.length} watches above
          </div>
          <div style={{ fontSize: 14, color: 'var(--text2)', maxWidth: 420, margin: '0 auto', lineHeight: 1.7 }}>
            Type a nickname like <strong>"Hulk"</strong>, <strong>"Pepsi"</strong> or <strong>"Panda"</strong>, or pick a brand and model. Results with valuations appear instantly inside the search panel.
          </div>
        </div>
      )}
    </div>
  );
}
